import { ServiceFactory } from '@amira-rnd/student-shared-services/dist/services';
import { ApiClient } from '@amira-rnd/student-shared-services/dist/utils';
import { AmiraAssignmentSubject } from '@amira-rnd/student-shared-services/dist/enums';
import { getUserData } from '../API';
import { transformAssignment } from './getAssignment/getAssignment';
import { graphQLRequestIAM } from '../graphQLService';

// Simple ErrorHandler implementation for the lexa app
class ErrorHandler {
    handleError(error, code) {
        console.error(`Error (${code}):`, error);
        // In a real implementation, you might want to send this to a logging service
    }
}

// IAM-enabled GraphQL client using AWS SigV4 signing
class IAMGraphQLClient {
    constructor(url) {
        this.url = url;
    }

    async query(options) {
        return graphQLRequestIAM(this.url, options.query, options.variables);
    }

    async mutate(options) {
        return graphQLRequestIAM(this.url, options.mutation, options.variables);
    }
}

// Legacy API key-based GraphQL client - kept for rollback if needed
/*
class CustomGraphQLClient {
    constructor(url, headers = {}) {
        this.url = url;
        this.headers = headers;
    }

    async query(options) {
        return this.request(options.query, options.variables, options.errorPolicy, options.fetchPolicy);
    }

    async mutate(options) {
        return this.request(options.mutation, options.variables, options.errorPolicy);
    }

    async request(query, variables = {}, errorPolicy = 'all', fetchPolicy = 'network-only') {
        const controller = new AbortController();
        const fetchTimeout = setTimeout(() => {
            controller.abort();
        }, 10000);

        console.log('Making GraphQL request to:', this.url);
        console.log('Request headers:', this.headers);
        console.log('Request body:', { query, variables });

        try {
            const response = await fetch(this.url, {
                method: 'POST',
                headers: {
                    ...this.headers,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ query, variables }),
                signal: controller.signal,
            });

            console.log('Response status:', response.status);
            console.log('Response headers:', Object.fromEntries(response.headers.entries()));

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Response error text:', errorText);
                throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
            }

            const result = await response.json();
            console.log('GraphQL response:', result);

            // Handle GraphQL errors based on error policy
            if (result.errors && errorPolicy === 'all') {
                return { data: result.data, errors: result.errors };
            } else if (result.errors) {
                throw new Error(result.errors[0].message);
            }

            return { data: result.data };
        } catch (error) {
            console.error('GraphQL request failed:', error);
            throw error;
        } finally {
            clearTimeout(fetchTimeout);
        }
    }
}*/

class AmiraAssignmentServiceManager {
    constructor() {
        this.initialized = false;
        this.amiraAssignmentService = null;
    }

    initialize(options = {}) {
        if (this.initialized) {
            return;
        }

        // Get user data for initialization
        const userData = getUserData();
        if (!userData || !userData.userId) {
            throw new Error('No user data available');
        }
        
        // Create user data object in the format expected by AmiraAssignmentService
        const userDataForService = {
            id: userData.userId,
            amiraSchoolId: userData.schoolId || '',
            amiraDistrictId: userData.districtId || '',
            curriculumId: userData.curriculumId,
            automaticInstructAssignments: userData.automaticInstructAssignments,
            automaticTextSetAssignments: userData.automaticTextSetAssignments,
            grade: userData.actualGrade,
            subscriptions: userData.subscriptions,
            isNonInboxUser: options.isNonInboxUser
        };

        // Debug environment variables (IAM auth - no API keys needed)
        console.log('Environment variables:', {
            ASSIGNMENT_SERVICE_URL: process.env.ASSIGNMENT_SERVICE_URL,
            SKILLS_ENDPOINT_URL: process.env.SKILLS_ENDPOINT_URL,
            CURRICULUM_LESSON_PLAN_URL: process.env.CURRICULUM_LESSON_PLAN_URL,
            SIS_BASE_URL: process.env.SIS_BASE_URL,
            STUDENT_RECORD_STORE_URL: process.env.STUDENT_RECORD_STORE_URL,
            LEGACY_API_URL: process.env.LEGACY_API_URL
        });

        // Validate required environment variables for IAM authentication
        if (!process.env.ASSIGNMENT_SERVICE_URL) {
            throw new Error('ASSIGNMENT_SERVICE_URL environment variable is not set');
        }

        if (!process.env.SKILLS_ENDPOINT_URL) {
            throw new Error('SKILLS_ENDPOINT_URL environment variable is not set');
        }

        if (!process.env.CURRICULUM_LESSON_PLAN_URL) {
            throw new Error('CURRICULUM_LESSON_PLAN_URL environment variable is not set');
        }

        if (!process.env.SIS_BASE_URL) {
            throw new Error('SIS_BASE_URL environment variable is not set');
        }

        // Create IAM-authenticated GraphQL clients for each service
        const assignmentGraphQLClient = new IAMGraphQLClient(process.env.ASSIGNMENT_SERVICE_URL);
        const skillsGraphQLClient = new IAMGraphQLClient(process.env.SKILLS_ENDPOINT_URL);
        const curriculumGraphQLClient = new IAMGraphQLClient(process.env.CURRICULUM_LESSON_PLAN_URL);
        const sisGraphQLClient = new IAMGraphQLClient(process.env.SIS_BASE_URL);
        // SRS (Student Record Store) client — required by ServiceFactory since the 2026.6.22
        // upgrade. Intentionally NOT guarded like the clients above: SRS is only exercised by
        // mastery/usage/story-recommendation paths, never by assessment or reading resolution,
        // so a missing URL must not fail assignment resolution for every student. The client is
        // constructed lazily and would only error if an SRS method is actually invoked.
        const srsGraphQLClient = new IAMGraphQLClient(process.env.STUDENT_RECORD_STORE_URL);
        // Create Legacy API client
        const legacyApiClient = new ApiClient({
            baseUrl: process.env.LEGACY_API_URL
        });

        const errorHandler = new ErrorHandler();
        
        // Create ServiceFactory instance with all required IAM-authenticated clients
        const serviceFactory = new ServiceFactory({
            amiraAssignmentGraphQLClient: assignmentGraphQLClient,
            skillsMasteryGraphQLClient: skillsGraphQLClient,
            curriculumLessonPlanGraphQLClient: curriculumGraphQLClient,
            sisGraphQLClient: sisGraphQLClient,
            srsGraphQLClient: srsGraphQLClient,
            legacyApiClient: legacyApiClient,
            errorHandler: errorHandler
        });
        
        this.amiraAssignmentService = serviceFactory.createAmiraAssignmentService(userDataForService);
        
        this.initialized = true;
    }

    /**
     * Get the next Amira assignment for the current student
     * @param {Object} options - Optional parameters
     * @param {boolean} options.isNonInboxUser - Override the user's isNonInboxUser setting for this call
     * @returns {Promise<Object>} The next assignment or null if none found
     */
    async getNextAssignment(options = {}) {
        try {
            this.initialize(options);
            const assignment = await this.amiraAssignmentService.getNextAmiraAssignment();
            return transformAssignment(assignment);
        } catch (error) {
            console.error('Error getting next Amira assignment:', error);
            throw error;
        }
    }

    /**
     * Resolve the next incomplete English (READING) and Spanish (LECTURA) assessment
     * using the shared-services subject-aware API — the same sequence olp-student-experience
     * uses in loadStudentInbox(): load the inbox, apply the standard inbox filters (which
     * populates the cached filteredAssignments), then resolve per subject off that cache.
     *
     * Subject routing (by assignment tag) and completion filtering (excludes
     * DONE/COMPLETE/ERROR/UNASSIGNED manifest items) are owned entirely by the package; we
     * do not reimplement them here.
     *
     * @returns {Promise<{english: Object|null, spanish: Object|null}>} transformed next
     *   assignment per subject (null when none is pending/incomplete for that subject).
     */
    async resolveBilingualAssessments() {
        // initialize() is idempotent — a no-op if the earlier getNextAssignment({isNonInboxUser:true})
        // already built the service. Pass the same option explicitly so behavior doesn't depend on
        // call ordering if this is ever invoked standalone (this path is non-inbox only).
        this.initialize({ isNonInboxUser: true });
        const studentId = getUserData().userId?.toString();

        const inbox = await this.amiraAssignmentService.getStudentInboxForCurrentUser();
        await this.amiraAssignmentService.applyInboxFilters(inbox, studentId);

        const english = await this.amiraAssignmentService.getNextAssignmentForSubject(AmiraAssignmentSubject.READING);
        const spanish = await this.amiraAssignmentService.getNextAssignmentForSubject(AmiraAssignmentSubject.LECTURA);

        return {
            english: transformAssignment(english),
            spanish: transformAssignment(spanish),
        };
    }
}

// Create a singleton instance
const amiraAssignmentWrapper = new AmiraAssignmentServiceManager();

export default amiraAssignmentWrapper; 